import React from "react";
import { Box, Card, Skeleton, Typography, useTheme } from "@mui/material";

export interface MobileKpiCardProps {
  label: string;
  count: number;
  loading?: boolean;
  onClick?: () => void;
  icon?: React.ReactNode;
  accentColor?: string;
  badge?: string;
  active?: boolean;
}

export const MobileKpiCard: React.FC<MobileKpiCardProps> = ({
  label,
  count,
  loading = false,
  onClick,
  icon,
  accentColor = "#1a97f5",
  badge,
  active = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Card
      onClick={onClick}
      elevation={0}
      sx={{
        p: 2,
        margin: 0,
        borderRadius: "16px",
        cursor: onClick ? "pointer" : "default",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: 104,
        width: "100%",
        border: `1.5px solid ${
          active
            ? accentColor
            : isDark
              ? "rgba(255,255,255,0.12)"
              : `${accentColor}55`
        }`,
        backgroundColor: active
          ? isDark
            ? "rgba(26, 151, 245, 0.12)"
            : "rgba(26, 151, 245, 0.04)"
          : isDark
            ? "rgba(30, 41, 59, 0.85)"
            : "#ffffff",
        boxShadow: active
          ? `0 6px 20px ${accentColor}25`
          : isDark
            ? "0 4px 16px rgba(0,0,0,0.25)"
            : "0 2px 12px rgba(0,0,0,0.03)",
        transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
        userSelect: "none",
        "&:active": {
          transform: "scale(0.98)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3.5px",
          background: accentColor,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            fontSize: "0.75rem",
            textTransform: "uppercase",
            letterSpacing: "0.03em",
            color: isDark ? "rgba(255,255,255,0.75)" : "#475569",
            lineHeight: 1.3,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {loading ? <Skeleton width={90} height={18} /> : label}
        </Typography>
        {icon && (
          <Box
            sx={{
              color: accentColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 30,
              height: 30,
              borderRadius: "8px",
              backgroundColor: `${accentColor}15`,
              border: `1px solid ${accentColor}35`,
              fontSize: "1rem",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          mt: 1,
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            fontSize: "2rem",
            color: isDark ? "#ffffff" : "#0f172a",
            lineHeight: 1,
            letterSpacing: "-0.03em",
          }}
        >
          {loading ? (
            <Skeleton width={50} height={36} />
          ) : (
            count.toLocaleString()
          )}
        </Typography>

        {badge && (
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: "999px",
              fontSize: "0.68rem",
              fontWeight: 700,
              backgroundColor: `${accentColor}15`,
              color: accentColor,
            }}
          >
            {badge}
          </Box>
        )}
      </Box>
    </Card>
  );
};
